import { m as memoizeWith, i as iconDefinition } from './definition28.js';
import { V as VividElement, D as DOM, a as attr, n as nullableNumberConverter, o as observable, h as html, d as createRegisterFunction, f as defineVividComponent } from './vivid-element.js';
import { R as Reflector } from './Reflector.js';
import { a as applyMixinsWithObservables } from './applyMixinsWithObservables.js';
import { D as DelegatesAria } from './delegates-aria.js';
import { F as FormAssociated } from './form-associated.js';
import { F as FormElementSuccessText, a as FormElementHelperText, b as FormElementCharCount, e as errorText, f as formElements, g as getFeedbackTemplate } from './form-elements.js';
import { b as AffixIcon, a as affixIconTemplateFactory } from './affix.js';
import { s as styles } from './text-field.js';
import { c as classNames } from './class-names.js';
import { w as when } from './when.js';
import { s as slotted } from './slotted.js';

const byteToHex = [];
for (let i = 0; i < 256; ++i) {
    byteToHex.push((i + 0x100).toString(16).slice(1));
}
function unsafeStringify(arr, offset = 0) {
    return (byteToHex[arr[offset + 0]] +
        byteToHex[arr[offset + 1]] +
        byteToHex[arr[offset + 2]] +
        byteToHex[arr[offset + 3]] +
        '-' +
        byteToHex[arr[offset + 4]] +
        byteToHex[arr[offset + 5]] +
        '-' +
        byteToHex[arr[offset + 6]] +
        byteToHex[arr[offset + 7]] +
        '-' +
        byteToHex[arr[offset + 8]] +
        byteToHex[arr[offset + 9]] +
        '-' +
        byteToHex[arr[offset + 10]] +
        byteToHex[arr[offset + 11]] +
        byteToHex[arr[offset + 12]] +
        byteToHex[arr[offset + 13]] +
        byteToHex[arr[offset + 14]] +
        byteToHex[arr[offset + 15]]).toLowerCase();
}

let getRandomValues;
const rnds8 = new Uint8Array(16);
function rng() {
    if (!getRandomValues) {
        if (typeof crypto === 'undefined' || !crypto.getRandomValues) {
            throw new Error('crypto.getRandomValues() not supported. See https://github.com/uuidjs/uuid#getrandomvalues-not-supported');
        }
        getRandomValues = crypto.getRandomValues.bind(crypto);
    }
    return getRandomValues(rnds8);
}

const randomUUID = typeof crypto !== 'undefined' && crypto.randomUUID && crypto.randomUUID.bind(crypto);
const native = { randomUUID };

function v4(options, buf, offset) {
    if (native.randomUUID && true && !options) {
        return native.randomUUID();
    }
    options = options || {};
    const rnds = options.random ?? options.rng?.() ?? rng();
    if (rnds.length < 16) {
        throw new Error('Random bytes length must be >= 16');
    }
    rnds[6] = (rnds[6] & 0x0f) | 0x40;
    rnds[8] = (rnds[8] & 0x3f) | 0x80;
    return unsafeStringify(rnds);
}

const generateRandomId = () => v4();

class _TextField extends VividElement {
}
class FormAssociatedTextField extends FormAssociated(_TextField) {
  constructor() {
    super(...arguments);
    this.proxy = document.createElement("input");
  }
}

var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __typeError = (msg) => {
  throw TypeError(msg);
};
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result) __defProp(target, key, result);
  return result;
};
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet = (obj, member, value, setter) => (__accessCheck(obj, member, "write to private field"), member.set(obj, value), value);
var __privateMethod = (obj, member, method) => (__accessCheck(obj, member, "access private method"), method);
var _TextField_instances, handleLabelChange_fn, _reflectToInput, _helperTextMirrorEl, _helperTextSlotMutationObserver, updateHelperTextMutationObserver_fn, updateMirroredHelperText_fn;
const TextFieldType = {
  /**
   * A text TextField
   */
  text: "text"};
const safariWorkaroundClassName = "_vvd-3-text-field-safari-workaround";
const getSafariWorkaroundStyleSheet = memoizeWith(
  () => "",
  () => {
    const styleSheet = new CSSStyleSheet();
    const supportsReplaceSync = "replaceSync" in styleSheet;
    if (supportsReplaceSync) {
      styleSheet.replaceSync(`
.${safariWorkaroundClassName}::placeholder {
	opacity: 1 !important;
	-webkit-text-fill-color: var(--_low-ink-color) !important;
}`);
    }
    return styleSheet;
  }
);
const installSafariWorkaroundStyle = (forElement) => {
  const root = forElement.getRootNode();
  const workaroundStyleSheet = getSafariWorkaroundStyleSheet();
  const supportsAdoptedStyleSheets = "adoptedStyleSheets" in root;
  if (!supportsAdoptedStyleSheets) {
    return;
  }
  if (!root.adoptedStyleSheets.includes(workaroundStyleSheet)) {
    root.adoptedStyleSheets = [
      ...root.adoptedStyleSheets,
      workaroundStyleSheet
    ];
  }
};
let TextField = class extends DelegatesAria(FormAssociatedTextField) {
  constructor() {
    super(...arguments);
    __privateAdd(this, _TextField_instances);
    // eslint-disable-next-line @nrwl/nx/workspace/no-attribute-default-value
    this.type = TextFieldType.text;
    /**
     * @internal
     */
    this._labelEl = null;
    __privateAdd(this, _reflectToInput);
    __privateAdd(this, _helperTextMirrorEl);
    __privateAdd(this, _helperTextSlotMutationObserver);
  }
  /**
   * @internal
   */
  readOnlyChanged() {
    if (this.proxy instanceof HTMLInputElement) {
      this.proxy.readOnly = this.readOnly;
      this.validate();
    }
  }
  /**
   * @internal
   */
  autofocusChanged() {
    if (this.proxy instanceof HTMLInputElement) {
      this.proxy.autofocus = this.autofocus;
      this.validate();
    }
  }
  /**
   * @internal
   */
  placeholderChanged() {
    if (this.proxy instanceof HTMLInputElement) {
      this.proxy.placeholder = this.placeholder;
    }
  }
  /**
   * @internal
   */
  typeChanged() {
    if (this.proxy instanceof HTMLInputElement) {
      this.proxy.type = this.type;
      this.validate();
    }
  }
  /**
   * @internal
   */
  listChanged() {
    if (this.proxy instanceof HTMLInputElement) {
      this.proxy.setAttribute("list", this.list);
      this.validate();
    }
  }
  /**
   * @internal
   */
  maxlengthChanged() {
    if (this.proxy instanceof HTMLInputElement) {
      this.proxy.maxLength = this.maxlength;
      this.validate();
    }
  }
  /**
   * @internal
   */
  minlengthChanged() {
    if (this.proxy instanceof HTMLInputElement) {
      this.proxy.minLength = this.minlength;
      this.validate();
    }
  }
  /**
   * @internal
   */
  patternChanged() {
    if (this.proxy instanceof HTMLInputElement) {
      this.proxy.pattern = this.pattern;
      this.validate();
    }
  }
  /**
   * @internal
   */
  sizeChanged() {
    if (this.proxy instanceof HTMLInputElement) {
      this.proxy.size = this.size;
    }
  }
  /**
   * @internal
   */
  spellcheckChanged() {
    if (this.proxy instanceof HTMLInputElement) {
      this.proxy.spellcheck = this.spellcheck;
    }
  }
  /**
   * Selects all the text in the text field
   *
   * @public
   */
  select() {
    this.control.select();
  }
  /**
   * Handles the internal control's `input` event
   * @internal
   */
  handleTextInput() {
    this.value = this.control.value;
  }
  /**
   * Change event handler for inner control.
   * @remarks
   * "Change" events are not `composable` so they will not
   * permeate the shadow DOM boundary. This fn effectively proxies
   * the change event, emitting a `change` event whenever the internal
   * control emits a `change` event
   * @internal
   */
  handleChange() {
    this.$emit("change");
  }
  /** {@inheritDoc (FormAssociated:interface).validate} */
  validate() {
    super.validate(this.control);
  }
  /**
   * @internal
   */
  labelChanged() {
    if (this._labelEl) {
      __privateMethod(this, _TextField_instances, handleLabelChange_fn).call(this, this._labelEl);
    }
  }
  connectedCallback() {
    super.connectedCallback();
    this.proxy.setAttribute("type", this.type);
    this.validate();
    if (this.autofocus) {
      DOM.queueUpdate(() => {
        this.focus();
      });
    }
    if (!this.control) {
      const uniqueId = this.id || generateRandomId();
      const controlId = `vvd-text-field-control-${uniqueId}`;
      const helperTextId = `vvd-text-field-helper-text-${uniqueId}`;
      const input = document.createElement("input");
      input.slot = "_control";
      input.id = controlId;
      input.className = safariWorkaroundClassName;
      this.control = input;
      input.addEventListener("input", () => {
        this.handleTextInput();
      });
      input.addEventListener("change", () => {
        this.handleChange();
      });
      input.addEventListener("blur", () => {
        this.$emit("blur", void 0, { bubbles: false });
      });
      input.addEventListener("focus", () => {
        this.$emit("focus", void 0, { bubbles: false });
      });
      this.appendChild(input);
      const label = document.createElement("label");
      label.slot = "_label";
      label.htmlFor = controlId;
      this._labelEl = label;
      __privateMethod(this, _TextField_instances, handleLabelChange_fn).call(this, label);
      __privateSet(this, _helperTextMirrorEl, document.createElement("div"));
      __privateGet(this, _helperTextMirrorEl).id = helperTextId;
      __privateGet(this, _helperTextMirrorEl).slot = "_mirrored-helper-text";
      __privateMethod(this, _TextField_instances, updateMirroredHelperText_fn).call(this);
      this.appendChild(__privateGet(this, _helperTextMirrorEl));
      installSafariWorkaroundStyle(this);
    }
    __privateSet(this, _reflectToInput, new Reflector(this, this.control));
    __privateGet(this, _reflectToInput).booleanAttribute("autofocus", "autofocus");
    __privateGet(this, _reflectToInput).booleanAttribute("disabled", "disabled");
    __privateGet(this, _reflectToInput).booleanAttribute("readOnly", "readonly");
    __privateGet(this, _reflectToInput).booleanAttribute("required", "required");
    __privateGet(this, _reflectToInput).booleanAttribute("spellcheck", "spellcheck");
    __privateGet(this, _reflectToInput).attribute("list", "list");
    __privateGet(this, _reflectToInput).attribute("maxlength", "maxlength");
    __privateGet(this, _reflectToInput).attribute("minlength", "minlength");
    __privateGet(this, _reflectToInput).attribute("pattern", "pattern");
    __privateGet(this, _reflectToInput).attribute("placeholder", "placeholder");
    __privateGet(this, _reflectToInput).attribute("size", "size");
    __privateGet(this, _reflectToInput).attribute("autoComplete", "autocomplete");
    __privateGet(this, _reflectToInput).attribute("type", "type");
    __privateGet(this, _reflectToInput).attribute("inputMode", "inputmode");
    __privateGet(this, _reflectToInput).attribute("ariaAtomic", "aria-atomic");
    __privateGet(this, _reflectToInput).attribute("ariaBusy", "aria-busy");
    __privateGet(this, _reflectToInput).attribute("ariaCurrent", "aria-current");
    __privateGet(this, _reflectToInput).attribute("ariaDisabled", "aria-disabled");
    __privateGet(this, _reflectToInput).attribute("ariaHasPopup", "aria-haspopup");
    __privateGet(this, _reflectToInput).attribute("ariaHidden", "aria-hidden");
    __privateGet(this, _reflectToInput).attribute("ariaInvalid", "aria-invalid");
    __privateGet(this, _reflectToInput).attribute("ariaKeyShortcuts", "aria-keyshortcuts");
    __privateGet(this, _reflectToInput).attribute("ariaLabel", "aria-label");
    __privateGet(this, _reflectToInput).attribute("ariaLive", "aria-live");
    __privateGet(this, _reflectToInput).attribute("ariaRelevant", "aria-relevant");
    __privateGet(this, _reflectToInput).attribute(
      "ariaRoleDescription",
      "aria-roledescription"
    );
    __privateGet(this, _reflectToInput).property("value", "value", true);
    __privateMethod(this, _TextField_instances, updateHelperTextMutationObserver_fn).call(this);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    __privateGet(this, _reflectToInput).destroy();
    __privateSet(this, _reflectToInput, void 0);
    __privateMethod(this, _TextField_instances, updateHelperTextMutationObserver_fn).call(this);
  }
  focus() {
    this.control?.focus();
  }
  /**
   * @internal
   */
  helperTextChanged() {
    __privateMethod(this, _TextField_instances, updateMirroredHelperText_fn).call(this);
  }
  /**
   * @internal
   */
  _helperTextSlottedContentChanged() {
    __privateMethod(this, _TextField_instances, updateMirroredHelperText_fn).call(this);
    __privateMethod(this, _TextField_instances, updateHelperTextMutationObserver_fn).call(this);
  }
};
_TextField_instances = new WeakSet();
handleLabelChange_fn = function(labelEl) {
  if (!this.label) {
    labelEl.remove();
  } else {
    labelEl.textContent = this.label;
    if (!labelEl.isConnected) {
      this.appendChild(labelEl);
    }
  }
};
_reflectToInput = new WeakMap();
_helperTextMirrorEl = new WeakMap();
_helperTextSlotMutationObserver = new WeakMap();
updateHelperTextMutationObserver_fn = function() {
  const usesHelperTextSlot = this._helperTextSlottedContent.length;
  const shouldHaveMutationObserver = usesHelperTextSlot && this.isConnected;
  if (shouldHaveMutationObserver && !__privateGet(this, _helperTextSlotMutationObserver)) {
    __privateSet(this, _helperTextSlotMutationObserver, new MutationObserver((records) => {
      if (records.some((record) => record.target !== __privateGet(this, _helperTextMirrorEl))) {
        __privateMethod(this, _TextField_instances, updateMirroredHelperText_fn).call(this);
      }
    }));
    __privateGet(this, _helperTextSlotMutationObserver).observe(this, {
      subtree: true,
      childList: true,
      characterData: true
    });
  }
  if (!shouldHaveMutationObserver && __privateGet(this, _helperTextSlotMutationObserver)) {
    __privateGet(this, _helperTextSlotMutationObserver).disconnect();
    __privateSet(this, _helperTextSlotMutationObserver, void 0);
  }
};
updateMirroredHelperText_fn = function() {
  let helperText = this.helperText ?? "";
  if (this._helperTextSlottedContent?.length) {
    helperText = this._helperTextSlottedContent.map((node) => node.innerText).join(" ");
  }
  if (__privateGet(this, _helperTextMirrorEl)) {
    __privateGet(this, _helperTextMirrorEl).textContent = helperText;
    if (helperText) {
      this.control.setAttribute(
        "aria-describedby",
        __privateGet(this, _helperTextMirrorEl).id
      );
    } else {
      this.control.removeAttribute("aria-describedby");
    }
  }
};
__decorateClass([
  attr({ attribute: "readonly", mode: "boolean" })
], TextField.prototype, "readOnly", 2);
__decorateClass([
  attr({ mode: "boolean" })
], TextField.prototype, "autofocus", 2);
__decorateClass([
  attr
], TextField.prototype, "placeholder", 2);
__decorateClass([
  attr
], TextField.prototype, "type", 2);
__decorateClass([
  attr
], TextField.prototype, "list", 2);
__decorateClass([
  attr({ converter: nullableNumberConverter })
], TextField.prototype, "maxlength", 2);
__decorateClass([
  attr({ converter: nullableNumberConverter })
], TextField.prototype, "minlength", 2);
__decorateClass([
  attr
], TextField.prototype, "pattern", 2);
__decorateClass([
  attr({ converter: nullableNumberConverter })
], TextField.prototype, "size", 2);
__decorateClass([
  attr({ mode: "boolean" })
], TextField.prototype, "spellcheck", 2);
__decorateClass([
  attr
], TextField.prototype, "appearance", 2);
__decorateClass([
  attr
], TextField.prototype, "shape", 2);
__decorateClass([
  attr
], TextField.prototype, "autoComplete", 2);
__decorateClass([
  attr()
], TextField.prototype, "scale", 2);
__decorateClass([
  attr({ attribute: "inputmode" })
], TextField.prototype, "inputMode", 2);
__decorateClass([
  observable
], TextField.prototype, "actionItemsSlottedContent", 2);
__decorateClass([
  observable
], TextField.prototype, "leadingActionItemsSlottedContent", 2);
TextField = __decorateClass([
  errorText,
  formElements
], TextField);
applyMixinsWithObservables(
  TextField,
  AffixIcon,
  FormElementCharCount,
  FormElementHelperText,
  FormElementSuccessText
);

const getStateClasses = ({
  errorValidationMessage,
  disabled,
  value,
  readOnly,
  placeholder,
  appearance,
  shape,
  label,
  successText,
  actionItemsSlottedContent,
  leadingActionItemsSlottedContent,
  icon,
  scale
}) => classNames(
  ["error", Boolean(errorValidationMessage)],
  ["disabled", disabled],
  ["has-value", Boolean(value)],
  ["readonly", readOnly],
  ["placeholder", Boolean(placeholder)],
  [`appearance-${appearance}`, Boolean(appearance)],
  [`shape-${shape}`, Boolean(shape)],
  ["no-label", !label],
  ["has-icon", !!icon],
  ["success", Boolean(successText)],
  ["action-items", !!actionItemsSlottedContent?.length],
  ["leading-action-items", !!leadingActionItemsSlottedContent?.length],
  ["no-leading", !(leadingActionItemsSlottedContent?.length || icon)],
  [`size-${scale}`, Boolean(scale)]
);
function renderCharCount() {
  return html`
		<span class="char-count"
			>${(x) => x.value ? x.value.length : 0} / ${(x) => x.maxlength}</span
		>
	`;
}
const TextfieldTemplate = (context) => {
  const affixIconTemplate = affixIconTemplateFactory(context);
  return html` <div class="base ${getStateClasses}">
			${when((x) => x.charCount && x.maxlength, renderCharCount())}
			<slot class="label" name="_label"></slot>
			<div class="fieldset">
				<div class="leading-items-wrapper">
					<slot
						name="leading-action-items"
						${slotted("leadingActionItemsSlottedContent")}
					></slot>
					${(x) => affixIconTemplate(x.icon)}
				</div>

				<div class="wrapper">
					<slot class="control" name="_control"></slot>
				</div>
				<div class="action-items-wrapper">
					<slot
						name="action-items"
						${slotted("actionItemsSlottedContent")}
					></slot>
				</div>
			</div>
			${getFeedbackTemplate(context)}
		</div>
		<slot name="_mirrored-helper-text"></slot>`;
};

const textFieldDefinition = defineVividComponent(
  "text-field",
  TextField,
  TextfieldTemplate,
  [iconDefinition],
  {
    styles,
    shadowOptions: {
      delegatesFocus: true
    }
  }
);
const registerTextField = createRegisterFunction(textFieldDefinition);

export { TextField as T, registerTextField as r, textFieldDefinition as t };
